import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { Close } from '@barracuda-internal/bds-core/dist/Icons/Core';
import { actions } from './globalReducer';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const UsersContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const { users, userDetail } = useSelector((state) => state.global);
  useEffect(() => {
    dispatch(actions.fetchUsers());
  }, [dispatch]);

  const handleRowClick = (id) => dispatch(actions.fetchUserDetail(id));
  const handleClose = () => dispatch(actions.closeUserDetail());

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Typography
        variant="h2"
        sx={{ marginBottom: tokens.aliasesSpaceContainerDefault }}
      >
        Users
      </Typography>

      {users.error && (
        <Typography
          variant="body1"
          sx={{
            color: tokens.aliasesColorTextError,
            marginBottom: tokens.aliasesSpaceContainerDefault,
          }}
        >
          {users.error}
        </Typography>
      )}

      {users.isLoading && users.items.length === 0 ? (
        <Typography variant="body1">Loading…</Typography>
      ) : (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
            '& th, & td': {
              padding: tokens.aliasesSpaceContainerDefault,
              borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
              textAlign: 'left',
            },
            '& th': { color: tokens.aliasesColorTextStaticSubtle },
            '& tbody tr': { cursor: 'pointer' },
            '& tbody tr:hover': {
              backgroundColor: tokens.aliasesColorBgInteractiveContainerSubtleDefault,
            },
          }}
        >
          <thead>
            <tr>
              <th><Typography variant="body1Bold">Email</Typography></th>
              <th><Typography variant="body1Bold">Name</Typography></th>
              <th><Typography variant="body1Bold">Accounts</Typography></th>
              <th><Typography variant="body1Bold">Last login</Typography></th>
            </tr>
          </thead>
          <tbody>
            {users.items.map((u) => (
              <tr key={u.id} onClick={() => handleRowClick(u.id)}>
                <td><Typography variant="body1">{u.username}</Typography></td>
                <td><Typography variant="body1">{u.name || '—'}</Typography></td>
                <td><Typography variant="body1">{u.account_count ?? 0}</Typography></td>
                <td><Typography variant="captionSubtle">{fmtDate(u.last_login)}</Typography></td>
              </tr>
            ))}
            {users.items.length === 0 && !users.isLoading && (
              <tr>
                <td colSpan={4}>
                  <Typography variant="captionSubtle">No users yet.</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={Boolean(userDetail.data || userDetail.isLoading)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 480 } }}
      >
        <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: tokens.aliasesSpaceContainerDefault,
            }}
          >
            <Typography variant="h3">User details</Typography>
            <IconButton bdsType="interactiveSubtle" onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>

          {userDetail.isLoading && (
            <Typography variant="body1">Loading…</Typography>
          )}
          {userDetail.error && (
            <Typography variant="body1" sx={{ color: tokens.aliasesColorTextError }}>
              {userDetail.error}
            </Typography>
          )}
          {userDetail.data && (
            <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
              <Box>
                <Typography variant="captionSubtle">Email</Typography>
                <Typography variant="body1">{userDetail.data.username}</Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Name</Typography>
                <Typography variant="body1">{userDetail.data.name || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Last login</Typography>
                <Typography variant="body1">{fmtDate(userDetail.data.last_login)}</Typography>
              </Box>

              <Box>
                <Typography
                  variant="captionSubtle"
                  sx={{ marginBottom: tokens.aliasesSpaceComponentDense, display: 'block' }}
                >
                  Account memberships ({userDetail.data.memberships?.length ?? 0})
                </Typography>
                {userDetail.data.memberships?.length === 0 ? (
                  <Typography variant="captionSubtle">No memberships.</Typography>
                ) : (
                  <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                    {userDetail.data.memberships?.map((m) => (
                      <Stack
                        key={m.account_id}
                        direction="row"
                        sx={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: tokens.aliasesSpaceComponentDefault,
                        }}
                      >
                        <Box>
                          <Typography variant="body1">{m.account_name}</Typography>
                          <Typography variant="captionSubtle">
                            {m.ai_guardian_enabled ? 'Enabled' : 'Not enabled'} · ID {m.account_id}
                          </Typography>
                        </Box>
                        <Chip
                          label={m.role.replace('account_', '')}
                          bdsType={m.role === 'account_admin' ? 'staticEmphasis' : 'staticNeutral'}
                          bdsSize="medium"
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default UsersContainer;
